package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.forecast.DayForecast
import com.evoapps.evoshift.domain.forecast.ForecastStatus
import com.evoapps.evoshift.domain.forecast.HeadcountRules
import com.evoapps.evoshift.domain.forecast.HeadcountSuggestion
import com.evoapps.evoshift.domain.forecast.SalesForecast
import com.evoapps.evoshift.domain.forecast.SalesForecaster
import com.evoapps.evoshift.domain.forecast.SalesHistoryDay
import com.evoapps.evoshift.domain.forecast.SalesPerHourSource
import com.evoapps.evoshift.domain.forecast.SlotWindow
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.GetDayClosuresUseCase
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn

/** One day's suggestion: the target and, when a sales-per-hour figure is known, the labour it carries. */
data class DaySuggestion(
    val forecast: DayForecast,
    val headcount: HeadcountSuggestion?
) {
    val date: LocalDate get() = forecast.date
    val amount: Double get() = forecast.amount
}

/** A month's suggested daily targets (SH-120); [status] says when there is too little history. */
data class MonthForecast(
    val status: ForecastStatus,
    val days: List<DaySuggestion>,
    val trendPercent: Int = 0,
    val historyWeeks: Int = 0
) {
    val byDate: Map<LocalDate, DaySuggestion> by lazy { days.associateBy { it.date } }

    /** The suggested net sales by day, for the staffing hints of days without a target. */
    val amounts: Map<LocalDate, Double> get() = days.associate { it.date to it.amount }

    companion object {
        /**
         * The suggestions of [forecast] with each day's headcount at [salesPerHour] (null: none)
         * spread over [slots].
         */
        fun of(
            forecast: SalesForecast,
            salesPerHour: Pair<Double, SalesPerHourSource>?,
            slots: List<SlotWindow>
        ): MonthForecast = MonthForecast(
            status = forecast.status,
            days = forecast.days.map { day ->
                DaySuggestion(day, salesPerHour?.let { (rate, source) -> HeadcountRules.suggest(day.amount, rate, source, slots) })
            },
            trendPercent = forecast.trendPercent,
            historyWeeks = forecast.historyWeeks
        )
    }
}

/**
 * SH-120: the suggested daily targets of a roster month from the workplace's own takings
 * ([SalesForecaster]), with the headcount they carry ([HeadcountRules]). Money, so null for those
 * without VIEW_FINANCES, and nothing is read for them. Days before [today] and the days the
 * workplace is closed get no suggestion; the history is the [HISTORY_MONTHS] months before this
 * one and this month's takings so far.
 */
class GetSalesForecastUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val financialRepository: FinancialRepository,
    private val getDayClosures: GetDayClosuresUseCase = GetDayClosuresUseCase(),
    private val caller: CallerCapabilities? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) {
    suspend operator fun invoke(rosterId: String): Result<MonthForecast?> = runCatching {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching null
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        // A month already over has nothing left to suggest: nothing is read.
        val lastDay = LocalDate(year, month, 1).plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1))
        if (lastDay < today()) return@runCatching MonthForecast(ForecastStatus.READY, emptyList())
        coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrThrow() }
            val financials = async { workplaceRepository.getWorkplaceFinancials(workplaceId).getOrThrow() }
            // A month without records (before the first one) is no history, not an error.
            val records = (0..HISTORY_MONTHS).map { back ->
                async { financialRepository.getDailyRecordsForRoster(monthBefore(workplaceId, year, month, back)).getOrDefault(emptyList()) }
            }
            val wp = workplace.await()
            val closed = getDayClosures(wp.settings, year, month).keys
            forecast(wp, financials.await(), records.awaitAll().flatten(), year, month, closed, today())
        }
    }

    /** The month's suggestions from the loaded records (only the trading Sundays are read here). */
    internal suspend fun forecast(
        workplace: Workplace,
        financials: WorkplaceFinancials,
        records: List<DailyRecord>,
        year: Int,
        month: Int,
        closed: Set<LocalDate>,
        today: LocalDate
    ): MonthForecast {
        val history = records.mapNotNull { it.historyDay() }.filter { it.date < today }
        val days = generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
            .takeWhile { it.month.number == month }
            .filter { it >= today && it !in closed }
            .toList()
        val years = (history.map { it.date.year } + days.map { it.year }).toSet()
        val rules = PayrollRules.of(workplace.settings.market)
        val holidays = years.flatMap { y ->
            generateSequence(LocalDate(y, 1, 1)) { it.plus(DatePeriod(days = 1)) }.takeWhile { it.year == y }.filter { rules.isHoliday(it) }
        }.toSet()
        val tradingSundays = days.map { it.year }.toSet().flatMap { y ->
            getDayClosures.calendar(workplace.settings.market.countryCode, y)?.tradingSundayDates.orEmpty()
        }.toSet()
        val forecast = SalesForecaster.forecast(history, days, holidays, tradingSundays)
        val firstDay = days.minOrNull() ?: today
        val rate = HeadcountRules.salesPerHour(financials.salesPerLabourHourGoal, history, firstDay)
        return MonthForecast.of(forecast, rate, workplace.settings.definedAvailabilitySlots.mapNotNull { it.window() })
    }

    companion object {
        /** Months of history before the forecast month (with this month's days so far): over 8 weeks. */
        const val HISTORY_MONTHS = 4

        private fun monthBefore(workplaceId: String, year: Int, month: Int, back: Int): String {
            val index = year * 12 + (month - 1) - back
            return RosterIds.of(workplaceId, index / 12, index % 12 + 1)
        }
    }
}

/** A daily record with takings as a day of sales history (the hours actually worked; 0 when none were recorded). */
internal fun DailyRecord.historyDay(): SalesHistoryDay? {
    val sales = revenue.realNetto?.takeIf { it > 0 } ?: return null
    val date = runCatching { LocalDate.parse(date) }.getOrNull() ?: return null
    val hours = costs.actualHours.takeIf { it > 0 } ?: laborStats.actual.totalHours
    return SalesHistoryDay(date, sales, hours)
}

/** "06:00"-"12:00" as minutes; "24:00" is the end of the day; null when unreadable. */
internal fun AvailabilitySlot.window(): SlotWindow? {
    fun minutes(time: String): Int? {
        val parts = time.trim().split(":")
        val h = parts.getOrNull(0)?.toIntOrNull() ?: return null
        val m = parts.getOrNull(1)?.toIntOrNull() ?: 0
        return (h * 60 + m).takeIf { it in 0..24 * 60 }
    }
    val start = minutes(startTime) ?: return null
    val end = minutes(endTime) ?: return null
    return SlotWindow(id, label, start, end).takeIf { end > start }
}
